/**
 * @module features/clients/types
 * @description Frontend TypeScript contracts, Zod schemas, and TanStack Query DTOs for Client Management.
 */
import { z } from "zod";

// ============================================================================
// Core Enums & Literals
// ============================================================================

export const ClientStatusEnum = z.enum(["active", "suspended", "inactive"]);
export type ClientStatus = z.infer<typeof ClientStatusEnum>;

// ============================================================================
// Client Entity Schemas
// ============================================================================

export const ClientSummarySchema = z.object({
  id: z.string(),
  org_name: z.string().min(1).max(200),
  org_email_id: z.string().email(),
  org_contact_no: z.string().min(5).max(25),
  logo_url: z.string().default(""),
  theme_color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default("#2563EB"),
  status: ClientStatusEnum,
  max_user_limit: z.number().int().min(1),
  active_user_count: z.number().int().default(0),
  city: z.string(),
  state: z.string(),
  contact_person_name: z.string(),
  created_at: z.string(),
});
export type ClientSummary = z.infer<typeof ClientSummarySchema>;

export const ClientDetailSchema = z.object({
  id: z.string(),
  org_name: z.string().min(1).max(200),
  org_email_id: z.string().email(),
  org_contact_no: z.string().min(5).max(25),
  logo_url: z.string().default(""),
  theme_color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default("#2563EB"),
  status: ClientStatusEnum,
  max_user_limit: z.number().int().min(1),
  active_user_count: z.number().int().default(0),
  quota_utilization_percent: z.number().default(0),
  street: z.string().min(1).max(255),
  landmark: z.string().max(255).nullable().optional(),
  city: z.string().min(1).max(100),
  state: z.string().min(1).max(100),
  country: z.string().min(1).max(100).default("India"),
  pincode: z.string().min(2).max(20),
  contact_person_name: z.string().min(1).max(150),
  contact_person_email: z.string().email(),
  contact_person_mobile_no: z.string().min(5).max(25),
  notification_email_ids: z.array(z.string().email()).default([]),
  notification_mobile_nos: z.array(z.string()).default([]),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ClientDetail = z.infer<typeof ClientDetailSchema>;

// ============================================================================
// Form Input & Validation Schemas (React Hook Form)
// ============================================================================

export const ClientOnboardFormSchema = z.object({
  org_name: z.string().trim().min(2, "Organization name must be at least 2 characters").max(200),
  org_email_id: z.string().trim().email("Please enter a valid organization email address"),
  org_contact_no: z.string().trim().min(7, "Please enter a valid contact phone number").max(25),
  logo_url: z.string().trim().optional().default(""),
  theme_color: z
    .string()
    .trim()
    .regex(/^#[0-9a-fA-F]{6}$/, "Must be a valid 6-character hex color (e.g. #2563EB)")
    .default("#2563EB"),
  max_user_limit: z.coerce.number().int().min(1, "User limit must be at least 1").default(10),
  street: z.string().trim().min(3, "Street address is required").max(255),
  landmark: z.string().trim().max(255).optional().or(z.literal("")),
  city: z.string().trim().min(2, "City is required").max(100),
  state: z.string().trim().min(2, "State/Province is required").max(100),
  country: z.string().trim().min(2, "Country is required").max(100).default("India"),
  pincode: z.string().trim().min(3, "Pincode is required").max(20),
  contact_person_name: z.string().trim().min(2, "Contact person name is required").max(150),
  contact_person_email: z.string().trim().email("Please enter a valid contact person email"),
  contact_person_mobile_no: z.string().trim().min(7, "Please enter a valid mobile number").max(25),
  auto_provision_user: z.boolean().default(false),
  notification_email_ids_raw: z.string().optional().default(""), // comma-separated in form
  notification_mobile_nos_raw: z.string().optional().default(""), // comma-separated in form
});
export type ClientOnboardFormValues = z.infer<typeof ClientOnboardFormSchema>;

export const ClientUpdateFormSchema = ClientOnboardFormSchema.partial().omit({
  auto_provision_user: true,
});
export type ClientUpdateFormValues = z.infer<typeof ClientUpdateFormSchema>;

// ============================================================================
// User Association Schemas
// ============================================================================

export const ClientLinkedUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  email_id: z.string().email(),
  contact_number: z.string().optional().default(""),
  avatar_url: z.string().default(""),
  is_totp_enabled: z.boolean().default(false),
  created_at: z.string(),
});
export type ClientLinkedUser = z.infer<typeof ClientLinkedUserSchema>;

export const AvailableUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  email_id: z.string().email(),
  avatar_url: z.string().default(""),
});
export type AvailableUser = z.infer<typeof AvailableUserSchema>;

// ============================================================================
// Fleet Metrics Schema
// ============================================================================

export const ClientFleetStatsSchema = z.object({
  total_clients: z.number().int(),
  active_clients: z.number().int(),
  suspended_clients: z.number().int(),
  total_linked_users: z.number().int(),
  avg_quota_utilization_pct: z.number(),
});
export type ClientFleetStats = z.infer<typeof ClientFleetStatsSchema>;

// ============================================================================
// Client Portal White-Labeling Injection Contract
// ============================================================================

export interface ClientBrandingConfig {
  orgName: string;
  logoUrl: string;
  themeColor: string; // e.g. '#2563EB'
}

/**
 * Converts a 6-digit hex color (#RRGGBB) into an HSL triplet string `"H S% L%"`
 * compatible with Tailwind's `hsl(var(--brand) / <alpha-value>)` token system.
 */
function hexToHslChannels(hex: string): { h: number; s: number; l: number } | null {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return null;
  const intVal = parseInt(m[1], 16);
  const r = ((intVal >> 16) & 255) / 255;
  const g = ((intVal >> 8) & 255) / 255;
  const b = (intVal & 255) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Injects client-configured theme color dynamically into CSS variables for portal branding.
 * Sets `--color-brand-primary` as well as `--brand`, `--brand-soft`, and `--ring` HSL tokens.
 */
export function applyClientThemeColor(themeColor?: string | null): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (!themeColor || !/^#[0-9a-fA-F]{6}$/.test(themeColor.trim())) {
    return;
  }
  const cleanHex = themeColor.trim();
  root.style.setProperty("--color-brand-primary", cleanHex);
  root.style.setProperty("--color-brand-foreground", "#ffffff");
  const hsl = hexToHslChannels(cleanHex);
  if (hsl) {
    root.style.setProperty("--brand", `${hsl.h} ${hsl.s}% ${hsl.l}%`);
    root.style.setProperty("--brand-foreground", "0 0% 100%");
    root.style.setProperty("--brand-soft", `${hsl.h} ${Math.min(100, hsl.s)}% 95%`);
  }
  try {
    localStorage.setItem("client_theme_color", cleanHex);
  } catch {
    // Ignore storage quota / privacy mode errors
  }
}

/**
 * Resets custom client theme variables back to platform defaults.
 */
export function clearClientThemeColor(): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.style.removeProperty("--color-brand-primary");
  root.style.removeProperty("--color-brand-foreground");
  root.style.removeProperty("--brand");
  root.style.removeProperty("--brand-foreground");
  root.style.removeProperty("--brand-soft");
}
